import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useInView, useReducedMotion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { VerdictBadge, ScoreRing } from "@/components/DonkeyBits";
import { useCountUp } from "@/hooks/useCountUp";
import { VERDICT_DIMENSIONS } from "./homeData";

const OVERALL = 78;
const CALC_MS = 2100;

function Gauge({ dim, active, reduce }) {
  const v = useCountUp(dim.value, active, { duration: 1400, reduce });
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="text-donkey-warm/80">{dim.label}</span>
        <span className="font-mono text-donkey-warmorange">{v}</span>
      </div>
      <div className="h-2 rounded-full bg-donkey-line/70 overflow-hidden">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-donkey-orange to-donkey-deeporange"
          initial={{ width: 0 }}
          animate={{ width: active ? `${dim.value}%` : 0 }}
          transition={{ duration: 1.4, ease: "easeOut" }}
        />
      </div>
    </div>
  );
}

export function VerdictCalculator() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.4, once: false });
  const [calc, setCalc] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const overall = useCountUp(OVERALL, revealed, { duration: 1200, reduce });

  useEffect(() => {
    if (reduce) {
      setCalc(true);
      setRevealed(true);
      return;
    }
    if (inView) {
      setCalc(true);
      const id = setTimeout(() => setRevealed(true), CALC_MS);
      return () => clearTimeout(id);
    }
    setCalc(false);
    setRevealed(false);
  }, [inView, reduce]);

  return (
    <section ref={ref} className="relative overflow-hidden border-t border-donkey-line bg-donkey-deep">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="w-16 h-1 rounded-full bg-donkey-orange mb-6" />
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-donkey-warm">The Donkey's Verdict</h2>
          <p className="text-donkey-grey mt-4 leading-relaxed max-w-xl">
            No gut feel. The Donkey scores every dimension, weighs the trade-offs, and only then commits to a clear, honest call.
          </p>
          <div className="mt-8 space-y-4" data-testid="verdict-gauges">
            {VERDICT_DIMENSIONS.map((d) => (
              <Gauge key={d.label} dim={d} active={calc} reduce={reduce} />
            ))}
          </div>
        </div>

        <div className="relative rounded-3xl border border-donkey-orange/25 bg-donkey-black/60 p-8 flex flex-col items-center justify-center min-h-[300px]" data-testid="verdict-panel">
          <AnimatePresence mode="wait">
            {!revealed ? (
              <motion.div key="calc" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center text-center">
                <Loader2 className="w-10 h-10 text-donkey-orange animate-spin" />
                <p className="mt-4 font-heading font-semibold text-donkey-warm">Calculating verdict…</p>
                <p className="text-xs text-donkey-grey mt-1">Weighing fit, demand, reach and cost</p>
              </motion.div>
            ) : (
              <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center text-center">
                <ScoreRing value={overall} size={120} />
                <div className="mt-5">
                  <VerdictBadge label="WORTH TESTING" />
                </div>
                <p className="mt-4 text-sm text-donkey-warm/80 max-w-xs leading-relaxed">
                  Strong personal fit and real demand. Cheap enough to test now &mdash; so test it.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <p className="relative mx-auto max-w-7xl px-5 sm:px-8 pb-16 -mt-10 text-xs text-donkey-grey/70">Illustrative verdict &mdash; yours is built from your own answers and live research.</p>
    </section>
  );
}

export default VerdictCalculator;
